import { useGetInvestorCompanyMicrosite, useGetInvestorCompanyUpdates } from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Building2, Users, TrendingUp, FileText, Video, Clock, MessageCircle, ChevronDown } from "lucide-react";
import InfoTip from "@/components/info-tip";
import { StructuredThesis, hasStructuredThesis, type StructuredThesisData } from "@/components/structured-thesis";
import { useState } from "react";
import { ChetanClipsGrid } from "@/components/chetan-clips-grid";

const itemTypeIcons: Record<string, typeof TrendingUp> = {
  fact: MessageCircle,
  kpi_change: TrendingUp,
  milestone: Clock,
  risk: Clock,
  ask: MessageCircle,
  media: Video,
};

const itemTypeBg: Record<string, string> = {
  fact: "bg-blue-50 border-blue-200",
  kpi_change: "bg-emerald-50 border-emerald-200",
  milestone: "bg-violet-50 border-violet-200",
  risk: "bg-red-50 border-red-200",
  ask: "bg-amber-50 border-amber-200",
  media: "bg-pink-50 border-pink-200",
};

export default function InvestorMicrosite({ investorId, companyId }: { investorId: number; companyId: number }) {
  const { data, isLoading, error } = useGetInvestorCompanyMicrosite(investorId, companyId);
  const { data: updateTimeline, isLoading: timelineLoading } = useGetInvestorCompanyUpdates(investorId, companyId);

  if (isLoading) return (
    <div className="space-y-6">
      <Skeleton className="h-32" />
      <Skeleton className="h-48" />
      <Skeleton className="h-48" />
    </div>
  );

  if (error) return (
    <div className="p-8 text-center">
      <p className="text-lg font-serif font-bold text-destructive">Access Denied</p>
      <p className="text-sm text-muted-foreground mt-2">You do not have permission to view this company.</p>
    </div>
  );

  if (!data) return null;

  const { company, founders, thesis, tractionSnapshot, timeline, documents, mediaAssets, openAsks } = data;
  // thesisStructured isn't in the codegen client typing yet (jsonb not in api-zod);
  // it's served by the API at runtime, so cast through the response payload.
  const thesisStructured = (data as { thesisStructured?: StructuredThesisData | null }).thesisStructured ?? null;

  return (
    <div className="space-y-8" data-testid="page-investor-microsite">
      <Card className="border-t-4 border-t-primary overflow-hidden">
        <CardContent className="p-8">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-serif font-bold tracking-tight">{company.name}</h1>
                <InfoTip content="This is the company microsite — a curated profile showing only founder-approved information. It includes the investment thesis, founding team, traction KPIs, a timeline of key updates, relevant documents, and media. All data has been reviewed and approved before reaching this view." />
              </div>
              <p className="text-sm text-muted-foreground mt-1">{company.sector} &middot; {company.stage}</p>
              {company.description && <p className="text-sm mt-3 max-w-2xl">{company.description}</p>}
            </div>
            {company.website && (
              <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">
                {company.website}
              </a>
            )}
          </div>
          {(thesis || hasStructuredThesis(thesisStructured)) && (
            <div className="mt-6 p-4 bg-primary/5 rounded-lg border border-primary/10">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Investment Thesis</p>
              {hasStructuredThesis(thesisStructured) ? (
                <StructuredThesis structured={thesisStructured} variant="light" />
              ) : (
                <p className="text-sm whitespace-pre-line">{thesis}</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="updates">Update Timeline</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="media">Media</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6 mt-4">
          {founders?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-serif flex items-center gap-2">
                  <Users className="w-4 h-4" /> Team
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-3">
                  {founders.map((f: any) => (
                    <div key={f.id} className="flex items-start gap-3 p-3 border rounded-md">
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-sm font-bold">
                        {f.name.split(" ").map((n: string) => n[0]).join("")}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{f.name}</p>
                        <p className="text-xs text-muted-foreground">{f.role}</p>
                        {f.bio && <p className="text-xs mt-1">{f.bio}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {(tractionSnapshot?.length ?? 0) > 0 && tractionSnapshot && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-serif flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" /> Traction
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {tractionSnapshot.slice(0, 9).map((kpi: any) => (
                    <div key={kpi.id} className="p-4 bg-muted rounded-lg text-center">
                      <p className="text-xs text-muted-foreground">{kpi.metricName}</p>
                      <p className="text-xl font-serif font-bold mt-1">{kpi.value}</p>
                      {kpi.unit && <p className="text-[10px] text-muted-foreground">{kpi.unit}</p>}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {(timeline?.length ?? 0) > 0 && timeline && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-serif">Latest Intelligence</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {timeline.slice(0, 5).map((item: any) => {
                    const Icon = itemTypeIcons[item.type] || MessageCircle;
                    return (
                      <div key={item.id} className={`p-3 rounded-md border ${itemTypeBg[item.type] || "bg-muted"}`}>
                        <div className="flex items-start gap-2">
                          <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <div>
                            <Badge variant="outline" className="text-[10px] mb-1">{item.type}</Badge>
                            <p className="text-sm">{item.summary}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {(openAsks?.length ?? 0) > 0 && openAsks && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-serif">Open Asks</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {openAsks.map((ask: any) => (
                    <div key={ask.id} className="p-3 bg-amber-50 border border-amber-200 rounded-md">
                      <p className="text-sm font-medium">{ask.title}</p>
                      {ask.description && <p className="text-xs text-muted-foreground mt-1">{ask.description}</p>}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="updates" className="mt-4">
          {timelineLoading ? (
            <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-32" />)}</div>
          ) : !updateTimeline?.length ? (
            <Card><CardContent className="p-8 text-center text-muted-foreground">No published updates yet</CardContent></Card>
          ) : (
            <div className="space-y-6">
              {updateTimeline.map((entry: any) => (
                <Card key={entry.cycle.id}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base font-serif flex items-center justify-between">
                      <span>
                        {entry.cycle.cycleType} Update &mdash; {new Date(entry.cycle.dueDate).toLocaleDateString()}
                      </span>
                      {entry.cycle.investorPublishedAt && (
                        <span className="text-xs text-muted-foreground font-normal">
                          Published {new Date(entry.cycle.investorPublishedAt).toLocaleDateString()}
                        </span>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {entry.items.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No items in this update</p>
                    ) : (
                      <div className="space-y-2">
                        {entry.items.map((item: any) => {
                          const Icon = itemTypeIcons[item.type] || MessageCircle;
                          return (
                            <div key={item.id} className={`p-3 rounded-md border ${itemTypeBg[item.type] || "bg-muted"}`}>
                              <div className="flex items-start gap-2">
                                <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                <div>
                                  <Badge variant="outline" className="text-[10px] mb-1">{item.type}</Badge>
                                  <p className="text-sm">{item.summary}</p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="documents" className="mt-4">
          {!documents?.length ? (
            <Card><CardContent className="p-8 text-center text-muted-foreground">No documents shared</CardContent></Card>
          ) : (
            <div className="space-y-2">
              {documents.map((doc: any) => (
                <Card key={doc.id}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">{doc.title}</p>
                        <p className="text-xs text-muted-foreground">{doc.type}</p>
                      </div>
                    </div>
                    {doc.fileUrl && (
                      <a href={doc.fileUrl} className="text-xs text-primary hover:underline">View</a>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="media" className="mt-4 space-y-4">
          <ChetanClipsGrid endpoint={`api/public/companies/${companyId}/clips`} tone="dark" />
          {!mediaAssets?.length ? null : (
            <div className="grid md:grid-cols-2 gap-3">
              {mediaAssets.map((m: any) => (
                <Card key={m.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <Video className="w-4 h-4 text-muted-foreground mt-0.5" />
                      <div>
                        <p className="text-sm font-medium">{m.title}</p>
                        <p className="text-xs text-muted-foreground">{m.type}</p>
                        {m.url && (
                          <a href={m.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline mt-1 block">
                            View
                          </a>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
